import type { ConnectResult } from './types';

type TransportCallback = (data: ConnectResult) => void;
type TransportErrorCallback = (error: string) => void;

// ── WebSocket Transport ──

export class WsTransport {
  private ws: WebSocket | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private closed = false;

  constructor(
    private apiUrl: string,
    private sessionToken: string,
    private onConfirmed: TransportCallback,
    private onRejected: () => void,
    private onExpired: () => void,
    private onError: TransportErrorCallback,
  ) {}

  connect(): void {
    this.closed = false;
    const wsUrl = this.apiUrl
      .replace(/^http/, 'ws')
      .replace(/\/$/, '');
    const url = `${wsUrl}/walletconnect?sessionToken=${this.sessionToken}`;

    try {
      // Socket.IO uses polling upgrade — use native WS for simplicity in SDK
      // The BE gateway uses socket.io, so we connect via socket.io protocol
      this.connectSocketIO();
    } catch {
      this.onError('WebSocket connection failed');
    }
  }

  private connectSocketIO(): void {
    const baseUrl = this.apiUrl.replace(/\/$/, '');

    // Socket.IO handshake: first GET for sid, then upgrade to WS
    const pollingUrl = `${baseUrl}/walletconnect/?EIO=4&transport=polling`;

    fetch(pollingUrl)
      .then((res) => res.text())
      .then((body) => {
        // Socket.IO response format: "0{...json...}"
        const jsonStr = body.replace(/^\d+/, '');
        const handshake = JSON.parse(jsonStr);
        const sid = handshake.sid;

        const wsBase = baseUrl.replace(/^http/, 'ws');
        const wsUrl = `${wsBase}/walletconnect/?EIO=4&transport=websocket&sid=${sid}`;

        this.ws = new WebSocket(wsUrl);

        this.ws.onopen = () => {
          // Socket.IO upgrade probe
          this.ws!.send('2probe');
        };

        this.ws.onmessage = (event) => {
          const msg = event.data as string;

          if (msg === '3probe') {
            // Upgrade confirmed, send upgrade packet + join room
            this.ws!.send('5');
            // Emit connect event with sessionToken as query (already in URL)
            // Socket.IO auto-joins based on handshake query
            return;
          }

          // Handle Socket.IO event messages: 42["event_name", {...data}]
          if (msg.startsWith('42')) {
            try {
              const payload = JSON.parse(msg.slice(2));
              const [eventName, data] = payload;
              this.handleEvent(eventName, data);
            } catch {
              // ignore malformed
            }
            return;
          }

          // Ping from server
          if (msg === '2') {
            this.ws!.send('3');
          }
        };

        this.ws.onclose = () => {
          if (!this.closed) {
            this.scheduleReconnect();
          }
        };

        this.ws.onerror = () => {
          if (!this.closed) {
            this.scheduleReconnect();
          }
        };
      })
      .catch(() => {
        if (!this.closed) {
          this.scheduleReconnect();
        }
      });
  }

  private handleEvent(eventName: string, data: any): void {
    switch (eventName) {
      case 'session_confirmed':
        this.onConfirmed(data);
        this.close();
        break;
      case 'session_rejected':
        this.onRejected();
        this.close();
        break;
      case 'session_expired':
        this.onExpired();
        this.close();
        break;
    }
  }

  private scheduleReconnect(): void {
    if (this.closed || this.reconnectTimer) return;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connectSocketIO();
    }, 3000);
  }

  close(): void {
    this.closed = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }
}

// ── Long-Poll Fallback ──

export class PollingTransport {
  private timer: ReturnType<typeof setInterval> | null = null;
  private stopped = false;

  constructor(
    private apiUrl: string,
    private sessionToken: string,
    private onConfirmed: TransportCallback,
    private onRejected: () => void,
    private onExpired: () => void,
    private onError: TransportErrorCallback,
  ) {}

  start(): void {
    this.stopped = false;
    this.poll();
    this.timer = setInterval(() => this.poll(), 2000);
  }

  private async poll(): Promise<void> {
    if (this.stopped) return;

    try {
      const baseUrl = this.apiUrl.replace(/\/$/, '');
      const res = await fetch(
        `${baseUrl}/connect/status?token=${this.sessionToken}`,
      );
      if (!res.ok) return;

      const data = await res.json();

      switch (data.status) {
        case 'CONFIRMED':
          this.onConfirmed({ wallet: data.wallet, user: data.user });
          this.stop();
          break;
        case 'REJECTED':
          this.onRejected();
          this.stop();
          break;
        case 'EXPIRED':
          this.onExpired();
          this.stop();
          break;
      }
    } catch {
      // silently retry next interval
    }
  }

  stop(): void {
    this.stopped = true;
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }
}
